import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface TypingUser {
  id: string;
  name: string;
  isTyping: boolean;
}

export const useTypingIndicator = (chatId: string | null) => {
  const { user, profile } = useAuthContext();
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingUser>>(new Map());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to presence channel for typing indicators
  useEffect(() => {
    if (!chatId || !user) return;

    const channel = supabase.channel(`typing:${chatId}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const newTypingUsers = new Map<string, TypingUser>();
        
        Object.entries(state).forEach(([key, presences]) => {
          if (key !== user.id && Array.isArray(presences) && presences.length > 0) {
            const presence = presences[0] as unknown as { id: string; name: string; isTyping: boolean };
            if (presence.isTyping) {
              newTypingUsers.set(key, {
                id: presence.id,
                name: presence.name,
                isTyping: presence.isTyping,
              });
            }
          }
        });
        
        setTypingUsers(newTypingUsers);
      })
      .on("presence", { event: "join" }, ({ key, newPresences }) => {
        if (key !== user.id && newPresences.length > 0) {
          const presence = newPresences[0] as unknown as { id: string; name: string; isTyping: boolean };
          if (presence.isTyping) {
            setTypingUsers((prev) => {
              const next = new Map(prev);
              next.set(key, {
                id: presence.id,
                name: presence.name,
                isTyping: presence.isTyping,
              });
              return next;
            });
          }
        }
      })
      .on("presence", { event: "leave" }, ({ key }) => {
        setTypingUsers((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [chatId, user]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping: boolean) => {
    if (!channelRef.current || !user || !profile) return;

    try {
      await channelRef.current.track({
        id: user.id,
        name: profile.name || "User",
        isTyping,
      });
    } catch (error) {
      console.error("Error sending typing indicator:", error);
    }
  }, [user, profile]);

  // Start typing with auto-stop after 3 seconds of inactivity
  const startTyping = useCallback(() => {
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing indicator
    sendTypingIndicator(true);

    // Auto-stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 3000);
  }, [sendTypingIndicator]);

  // Stop typing immediately
  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    sendTypingIndicator(false);
  }, [sendTypingIndicator]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Get the first typing user's name (for display)
  const typingUserName = typingUsers.size > 0 
    ? Array.from(typingUsers.values())[0]?.name 
    : null;

  return {
    isOtherUserTyping: typingUsers.size > 0,
    typingUserName,
    startTyping,
    stopTyping,
  };
};
